// Mock data for demo dashboard

export interface Entity {
  entity_id: string;
  name: string;
  score: number;
  owners: string[];
  last_updated: string;
  trend: 'up' | 'down' | 'stable';
}

export interface Person {
  person_id: string;
  name: string;
  score: number;
  unique_knowledge_count: number;
  last_updated: string;
}

export interface GraphNode {
  id: string;
  name: string;
  type: 'person' | 'entity';
  score: number;
}

export type EdgeType = 'knows' | 'asked-about' | 'depends-on';

export interface GraphEdge {
  source: string;
  target: string;
  strength: number;
  type?: EdgeType;
}

export interface Intervention {
  intervention_id: string;
  type: 'question' | 'summary' | 'documentation';
  target_entity: string;
  target_person: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
  created_at: string;
  content: string;
  fragility_score_before?: number;
  fragility_score_after?: number;
  title: string;
  urgency: 'High' | 'Medium' | 'Low';
  description: string;
  scoreBefore?: number;
  scoreAfter?: number;
  answeredCount?: number;
  totalQuestions?: number;
  completionPercentage?: number;
}

export const mockEntities: Entity[] = [
  {
    entity_id: 'e1',
    name: 'Payment Processing',
    score: 92,
    owners: ['Raj Kumar'],
    last_updated: '2026-03-06T10:00:00Z',
    trend: 'up'
  },
  {
    entity_id: 'e2',
    name: 'CI/CD Pipeline',
    score: 85,
    owners: ['Priya Sharma'],
    last_updated: '2026-03-06T09:30:00Z',
    trend: 'stable'
  },
  {
    entity_id: 'e3',
    name: 'Database Migrations',
    score: 65,
    owners: ['Amit Patel'],
    last_updated: '2026-03-06T08:45:00Z',
    trend: 'down'
  },
  {
    entity_id: 'e4',
    name: 'API Authentication',
    score: 45,
    owners: ['John Smith', 'Sarah Chen'],
    last_updated: '2026-03-06T11:15:00Z',
    trend: 'stable'
  },
  {
    entity_id: 'e5',
    name: 'Frontend Testing',
    score: 15,
    owners: ['Sarah Chen', 'Mike Johnson', 'Emily Davis'],
    last_updated: '2026-03-06T10:30:00Z',
    trend: 'down'
  }
];

export const mockPeople: Person[] = [
  {
    person_id: 'p1',
    name: 'Raj Kumar',
    score: 88,
    unique_knowledge_count: 3,
    last_updated: '2026-03-06T10:00:00Z'
  },
  {
    person_id: 'p2',
    name: 'Priya Sharma',
    score: 82,
    unique_knowledge_count: 2,
    last_updated: '2026-03-06T09:30:00Z'
  },
  {
    person_id: 'p3',
    name: 'Amit Patel',
    score: 65,
    unique_knowledge_count: 4,
    last_updated: '2026-03-06T08:45:00Z'
  },
  {
    person_id: 'p4',
    name: 'John Smith',
    score: 42,
    unique_knowledge_count: 5,
    last_updated: '2026-03-06T11:15:00Z'
  },
  {
    person_id: 'p5',
    name: 'Sarah Chen',
    score: 38,
    unique_knowledge_count: 6,
    last_updated: '2026-03-06T10:30:00Z'
  },
  {
    person_id: 'p6',
    name: 'Mike Johnson',
    score: 25,
    unique_knowledge_count: 4,
    last_updated: '2026-03-06T10:30:00Z'
  },
  {
    person_id: 'p7',
    name: 'Emily Davis',
    score: 20,
    unique_knowledge_count: 3,
    last_updated: '2026-03-06T10:30:00Z'
  }
];

export const mockGraphNodes: GraphNode[] = [
  // People
  { id: 'p1', name: 'Raj Kumar', type: 'person', score: 88 },
  { id: 'p2', name: 'Priya Sharma', type: 'person', score: 82 },
  { id: 'p3', name: 'Amit Patel', type: 'person', score: 65 },
  { id: 'p4', name: 'John Smith', type: 'person', score: 42 },
  { id: 'p5', name: 'Sarah Chen', type: 'person', score: 38 },
  { id: 'p6', name: 'Mike Johnson', type: 'person', score: 25 },
  { id: 'p7', name: 'Emily Davis', type: 'person', score: 20 },
  // Entities
  { id: 'e1', name: 'Payment Processing', type: 'entity', score: 92 },
  { id: 'e2', name: 'CI/CD Pipeline', type: 'entity', score: 85 },
  { id: 'e3', name: 'Database Migrations', type: 'entity', score: 65 },
  { id: 'e4', name: 'API Authentication', type: 'entity', score: 45 },
  { id: 'e5', name: 'Frontend Testing', type: 'entity', score: 15 }
];

export const mockGraphEdges: GraphEdge[] = [
  // knows — person owns/knows a topic
  { source: 'p1', target: 'e1', strength: 1.0, type: 'knows' },
  { source: 'p2', target: 'e2', strength: 1.0, type: 'knows' },
  { source: 'p3', target: 'e3', strength: 0.8, type: 'knows' },
  { source: 'p4', target: 'e4', strength: 0.7, type: 'knows' },
  { source: 'p5', target: 'e4', strength: 0.7, type: 'knows' },
  { source: 'p5', target: 'e5', strength: 0.5, type: 'knows' },
  { source: 'p6', target: 'e5', strength: 0.5, type: 'knows' },
  { source: 'p7', target: 'e5', strength: 0.5, type: 'knows' },
  // asked-about — person has been asked about a topic repeatedly
  { source: 'p3', target: 'e2', strength: 0.4, type: 'asked-about' },
  { source: 'p1', target: 'e4', strength: 0.3, type: 'asked-about' },
  // depends-on — one topic depends on another's knowledge
  { source: 'e3', target: 'e2', strength: 0.6, type: 'depends-on' },
  { source: 'e4', target: 'e1', strength: 0.5, type: 'depends-on' },
];

export const mockInterventions: Intervention[] = [
  {
    intervention_id: 'i1',
    type: 'question',
    target_entity: 'Payment Processing',
    target_person: 'Raj Kumar',
    status: 'IN_PROGRESS',
    fragility_score_before: 92,
    scoreBefore: 92,
    answeredCount: 1,
    totalQuestions: 3,
    completionPercentage: 33,
    created_at: '2026-03-06T15:30:00Z',
    title: 'Payment Processing Knowledge Transfer',
    urgency: 'High',
    description: 'Document payment gateway integration and security considerations',
    content: 'AI-Generated Questions:\n\n1. Can you document the payment gateway integration flow, including error handling for failed transactions?\n\n2. What are the key security considerations when processing credit card data, and how do we ensure PCI compliance?\n\n3. How does the refund process work, and what edge cases should new team members be aware of?'
  },
  {
    intervention_id: 'i2',
    type: 'documentation',
    target_entity: 'Payment Processing',
    target_person: 'Raj Kumar',
    status: 'PENDING',
    fragility_score_before: 92,
    scoreBefore: 92,
    answeredCount: 0,
    completionPercentage: 0,
    created_at: '2026-03-06T10:05:00Z',
    title: 'Payment Processing Documentation',
    urgency: 'High',
    description: 'Create comprehensive documentation for payment system',
    content: 'AI-Generated Micro-Documentation:\n\n# Payment Processing System\n\n## Purpose\nHandles all payment transactions including credit cards, digital wallets, and bank transfers. Integrates with Stripe and PayPal APIs.\n\n## Key Concepts\n- **Idempotency Keys**: Prevent duplicate charges\n- **Webhook Handling**: Process async payment confirmations\n- **PCI Compliance**: Never store raw card data\n\n## Common Pitfalls\n- Forgetting to handle network timeouts (use 30s timeout)\n- Not validating webhook signatures (security risk)\n- Missing refund reconciliation in accounting\n\n## Related Resources\n- Stripe API Docs: /docs/stripe-integration.md\n- Payment Flow Diagram: /diagrams/payment-flow.png'
  },
  {
    intervention_id: 'i3',
    type: 'question',
    target_entity: 'CI/CD Pipeline',
    target_person: 'Priya Sharma',
    status: 'IN_PROGRESS',
    fragility_score_before: 85,
    scoreBefore: 85,
    answeredCount: 2,
    totalQuestions: 3,
    completionPercentage: 67,
    created_at: '2026-03-06T15:00:00Z',
    title: 'CI/CD Pipeline Knowledge Sharing',
    urgency: 'High',
    description: 'Document deployment stages and rollback procedures',
    content: 'AI-Generated Questions:\n\n1. Can you explain the deployment stages and what checks happen at each stage before production?\n\n2. How do we handle rollbacks when a deployment fails, and what monitoring alerts should trigger a rollback?\n\n3. What are the environment-specific configurations, and how do we manage secrets across dev/staging/prod?'
  },
  {
    intervention_id: 'i4',
    type: 'summary',
    target_entity: 'Database Migrations',
    target_person: 'Amit Patel',
    status: 'COMPLETED',
    fragility_score_before: 65,
    fragility_score_after: 57,
    scoreBefore: 65,
    scoreAfter: 57,
    answeredCount: 2,
    totalQuestions: 2,
    completionPercentage: 100,
    created_at: '2026-03-05T14:20:00Z',
    title: 'Database Migrations Overview',
    urgency: 'Medium',
    description: 'Explain database migration concepts to new team members',
    content: 'AI-Generated Summary (Explain Like I\'m New):\n\n## What is this?\nDatabase migrations are version-controlled changes to our database schema. Think of them like Git commits, but for your database structure.\n\n## Why does it matter?\nWithout migrations, every developer would have a different database structure, causing bugs and deployment failures. Migrations ensure everyone\'s database stays in sync.\n\n## Key Concepts\n- **Up Migration**: Applies changes (add table, add column)\n- **Down Migration**: Reverts changes (rollback)\n- **Migration Order**: Numbered files run in sequence\n\n## Common Scenarios\n- Adding a new feature that needs a new table\n- Modifying existing columns (careful with data loss!)\n- Creating indexes for performance\n\n## Related Resources\n- Alembic docs, Django migrations guide'
  },
  {
    intervention_id: 'i5',
    type: 'documentation',
    target_entity: 'CI/CD Pipeline',
    target_person: 'Priya Sharma',
    status: 'PENDING',
    fragility_score_before: 85,
    scoreBefore: 85,
    answeredCount: 0,
    completionPercentage: 0,
    created_at: '2026-03-06T09:35:00Z',
    title: 'CI/CD Pipeline Documentation',
    urgency: 'Medium',
    description: 'Document automated deployment pipeline',
    content: 'AI-Generated Micro-Documentation:\n\n# CI/CD Pipeline\n\n## Purpose\nAutomated build, test, and deployment pipeline using GitHub Actions and AWS CodeDeploy.\n\n## Key Concepts\n- **Build Stage**: Compile code, run linters, unit tests\n- **Test Stage**: Integration tests, security scans\n- **Deploy Stage**: Blue-green deployment to production\n\n## Common Pitfalls\n- Skipping tests to deploy faster (never do this!)\n- Not testing migrations in staging first\n- Forgetting to update environment variables\n\n## Related Resources\n- GitHub Actions workflow: .github/workflows/deploy.yml\n- Deployment runbook: /docs/deployment-guide.md'
  },
  {
    intervention_id: 'i6',
    type: 'question',
    target_entity: 'API Authentication',
    target_person: 'John Smith',
    status: 'PENDING',
    fragility_score_before: 45,
    fragility_score_after: 32,
    scoreBefore: 45,
    answeredCount: 0,
    completionPercentage: 0,
    created_at: '2026-03-05T16:00:00Z',
    title: 'API Authentication Documentation',
    urgency: 'High',
    description: 'Document authentication mechanisms and permission system',
    content: 'AI-Generated Questions:\n\n1. What authentication mechanisms are currently implemented (JWT, OAuth, API keys), and when should each be used?\n\n2. How do we handle token refresh and expiration, and what happens when a user\'s session expires mid-request?\n\n3. What are the rate limiting rules for different API endpoints, and how do we prevent abuse?\n\n4. Can you document the permission system and how roles are assigned to users?'
  },
  {
    intervention_id: 'i7',
    type: 'question',
    target_entity: 'Frontend Testing',
    target_person: 'Sarah Chen',
    status: 'PENDING',
    fragility_score_before: 15,
    fragility_score_after: 10,
    scoreBefore: 15,
    answeredCount: 0,
    completionPercentage: 0,
    created_at: '2026-03-05T11:30:00Z',
    title: 'Frontend Testing Best Practices',
    urgency: 'Medium',
    description: 'Document testing frameworks and best practices',
    content: 'AI-Generated Questions:\n\n1. What testing frameworks and libraries are we using (Jest, React Testing Library, Cypress), and what is each used for?\n\n2. How do we structure our test files, and what naming conventions should we follow?\n\n3. What are the best practices for mocking API calls and external dependencies in our tests?\n\n4. How do we run tests locally and in CI/CD, and what coverage thresholds must we meet?\n\n5. Can you provide examples of testing complex user interactions like form submissions and modal workflows?'
  },
  {
    intervention_id: 'i8',
    type: 'question',
    target_entity: 'Database Migrations',
    target_person: 'Amit Patel',
    status: 'PENDING',
    fragility_score_before: 65,
    scoreBefore: 65,
    answeredCount: 0,
    completionPercentage: 0,
    created_at: '2026-03-06T08:00:00Z',
    title: 'Database Migrations Deep Dive',
    urgency: 'High',
    description: 'Document step-by-step migration process and data migration strategies',
    content: 'AI-Generated Questions:\n\n1. What is the step-by-step process for creating a new database migration, from writing the migration file to deploying it?\n\n2. How do we handle data migrations when changing column types or moving data between tables?'
  }
];

export interface TeamMember {
  id: string;
  name: string;
  role: string;
}

export const mockOrgTeamMembers = {
  members: [
    { userId: 'p1', name: 'Raj Kumar',    email: 'raj.kumar@cloudstack.demo',    role: 'org-member' as const, joinedAt: '2026-01-15T00:00:00Z' },
    { userId: 'p2', name: 'Priya Sharma', email: 'priya.sharma@cloudstack.demo', role: 'org-member' as const, joinedAt: '2026-01-15T00:00:00Z' },
    { userId: 'p3', name: 'Amit Patel',   email: 'amit.patel@cloudstack.demo',   role: 'org-member' as const, joinedAt: '2026-01-20T00:00:00Z' },
    { userId: 'p4', name: 'John Smith',   email: 'john.smith@cloudstack.demo',   role: 'org-member' as const, joinedAt: '2026-02-01T00:00:00Z' },
    { userId: 'p5', name: 'Sarah Chen',   email: 'sarah.chen@cloudstack.demo',   role: 'org-member' as const, joinedAt: '2026-02-01T00:00:00Z' },
    { userId: 'p6', name: 'Emily Davis',  email: 'emily.davis@cloudstack.demo',  role: 'org-member' as const, joinedAt: '2026-02-10T00:00:00Z' },
  ],
  pendingInvitations: [],
};

export const mockTeamMembers: TeamMember[] = [
  { id: '1', name: 'Raj Kumar', role: 'Senior Backend Engineer' },
  { id: '2', name: 'Priya Sharma', role: 'DevOps Lead' },
  { id: '3', name: 'Amit Patel', role: 'Database Architect' },
  { id: '4', name: 'John Smith', role: 'Full Stack Developer' },
  { id: '5', name: 'Sarah Chen', role: 'Frontend Lead' },
  { id: '6', name: 'Mike Johnson', role: 'Backend Engineer' },
  { id: '7', name: 'Emily Davis', role: 'QA Engineer' },
];

export const getScoreColor = (score: number): string => {
  if (score >= 76) return 'text-red-400';
  if (score >= 51) return 'text-yellow-400';
  return 'text-green-400';
};

export const getScoreBgColor = (score: number): string => {
  if (score >= 76) return 'bg-red-900/20 border-red-500/30';
  if (score >= 51) return 'bg-yellow-900/20 border-yellow-500/30';
  return 'bg-green-900/20 border-green-500/30';
};

export const getScoreLabel = (score: number): string => {
  if (score >= 76) return 'CRITICAL';
  if (score >= 51) return 'HIGH';
  if (score >= 26) return 'MODERATE';
  return 'LOW';
};
